import {ListView} from '@github-ui/list-view'
import {testIdProps} from '@github-ui/test-id-props'
import {graphql, usePaginationFragment, type LoadMoreFn} from 'react-relay'
import styles from './RepositoryMilestone.module.css'
import {useMemo} from 'react'
import type {MilestoneList$data, MilestoneList$key} from './__generated__/MilestoneList.graphql'
import type {OperationType} from 'relay-runtime'
import {MilestoneRow} from './MilestoneRow'
import {OpenClosedMilestones} from './OpenClosedMilestones'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'

type MilestoneListProps = {
  repositoryRef: MilestoneList$key
}

export function MilestoneList({repositoryRef}: MilestoneListProps) {
  const {data, loadNext, hasNext} = usePaginationFragment(
    graphql`
      fragment MilestoneList on Repository
      @refetchable(queryName: "MilestoneListQuery")
      @argumentDefinitions(
        cursor: {type: "String", defaultValue: null}
        first: {type: "Int!"}
        state: {type: "MilestoneState!"}
      ) {
        milestones(first: $first, after: $cursor, states: [$state]) @connection(key: "MilestoneList_milestones") {
          edges {
            node {
              id
              ...MilestoneRow
            }
          }
        }
        ...OpenClosedMilestones
      }
    `,
    repositoryRef,
  )

  if (!data.milestones) {
    throw new Error('Milestones data is null or undefined')
  }

  return <MilestoneListInternal data={data} loadNext={loadNext} hasNext={hasNext} />
}

function MilestoneListInternal({
  data,
}: {
  data: MilestoneList$data
  hasNext: boolean
  loadNext: LoadMoreFn<OperationType>
}) {
  const nodes = useMemo(() => {
    return data.milestones?.edges?.map(edge => (edge?.node ? edge.node : null)).filter(node => !!node) || []
  }, [data])

  const listItemsHeader = <ListViewMetadata sectionFilters={<OpenClosedMilestones repository={data} />} />

  return (
    <>
      <div className={styles.milestoneListWrapper} data-hpc>
        <ListView
          {...testIdProps('repository-milestone-list-view')}
          title=""
          metadata={listItemsHeader}
          titleHeaderTag="h2"
          singularUnits={'milestone'}
          pluralUnits={'milestones'}
        >
          {nodes.map(node => (
            <MilestoneRow key={node.id} milestone={node} />
          ))}
        </ListView>
      </div>
    </>
  )
}

try{ MilestoneList.displayName ||= 'MilestoneList' } catch {}
try{ MilestoneListInternal.displayName ||= 'MilestoneListInternal' } catch {}